// src/components/MutedConversationsModal.tsx
import { logger } from '../utils/logger';
// Modal to view and unmute muted chats and groups

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { close, notifications } from 'ionicons/icons';
import { getMutedChats, getMutedGroups, unmuteChat, unmuteGroup } from '../services/chatPreferencesService';
import { supabase } from '../services/supabase';
import { AvatarPreview } from './avatar/AvatarPreview';

interface MutedConversationsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onUnmute?: () => void; // Callback to refresh parent
}

interface MutedChat {
  id: string;
  otherUser: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar: any;
  };
}

interface MutedGroup {
  id: string;
  name: string;
  member_count: number;
}

export const MutedConversationsModal: React.FC<MutedConversationsModalProps> = ({
  isOpen,
  onDismiss,
  onUnmute,
}) => {
  const [segment, setSegment] = useState<'chats' | 'groups'>('chats');
  const [mutedChats, setMutedChats] = useState<MutedChat[]>([]);
  const [mutedGroups, setMutedGroups] = useState<MutedGroup[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMutedItems();
    }
  }, [isOpen]);

  const loadMutedItems = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get muted chat IDs
      const mutedChatIds = await getMutedChats();

      // Fetch full chat details for muted chats
      if (mutedChatIds.length > 0) {
        const { data: matches } = await supabase
          .from('matches')
          .select(`
            id,
            user1_id,
            user2_id,
            user1:profiles!matches_user1_id_fkey (id, username, display_name, avatar_url, meeple_avatar),
            user2:profiles!matches_user2_id_fkey (id, username, display_name, avatar_url, meeple_avatar)
          `)
          .in('id', mutedChatIds);

        const formattedChats: MutedChat[] = (matches || []).map((match: any) => {
          const isUser1 = match.user1_id === user.id;
          const otherUser = isUser1 ? match.user2 : match.user1;
          return {
            id: match.id,
            otherUser: {
              id: otherUser?.id || '',
              username: otherUser?.username || '',
              display_name: otherUser?.display_name || '',
              avatar_url: otherUser?.avatar_url || null,
              avatar: otherUser?.meeple_avatar || null,
            },
          };
        });
        setMutedChats(formattedChats);
      } else {
        setMutedChats([]);
      }

      // Get muted group IDs
      const mutedGroupIds = await getMutedGroups();

      // Fetch full group details
      if (mutedGroupIds.length > 0) {
        const { data: groups } = await supabase
          .from('groups')
          .select('id, name, member_count:group_members(count)')
          .in('id', mutedGroupIds);

        const formattedGroups: MutedGroup[] = (groups || []).map(group => ({
          id: group.id,
          name: group.name,
          member_count: group.member_count?.[0]?.count || 0,
        }));
        setMutedGroups(formattedGroups);
      } else {
        setMutedGroups([]);
      }
    } catch (error) {
      logger.error('Error loading muted items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnmuteChat = async (chatId: string) => {
    const result = await unmuteChat(chatId);
    if (result.success) {
      setMutedChats(prev => prev.filter(c => c.id !== chatId));
      onUnmute?.();
    }
  };

  const handleUnmuteGroup = async (groupId: string) => {
    const result = await unmuteGroup(groupId);
    if (result.success) {
      setMutedGroups(prev => prev.filter(g => g.id !== groupId));
      onUnmute?.();
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss} aria-label="Close muted conversations">
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
          <IonTitle>Muted Conversations</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={segment} onIonChange={e => setSegment(e.detail.value as 'chats' | 'groups')}>
            <IonSegmentButton value="chats">
              <IonLabel>Chats ({mutedChats.length})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="groups">
              <IonLabel>Groups ({mutedGroups.length})</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner />
          </div>
        ) : segment === 'chats' ? (
          mutedChats.length === 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}
              role="status"
              aria-label="No muted chats"
            >
              <IonText color="medium">
                <p>No muted chats</p>
              </IonText>
            </div>
          ) : (
            <IonList>
              {mutedChats.map(chat => (
                <IonItem key={chat.id}>
                  <IonAvatar slot="start">
                    {chat.otherUser.avatar_url ? (
                      <img src={chat.otherUser.avatar_url} alt={chat.otherUser.display_name || chat.otherUser.username} />
                    ) : chat.otherUser.avatar ? (
                      <AvatarPreview
                        avatarData={chat.otherUser.avatar}
                        size={40}
                        userId={chat.otherUser.id}
                      />
                    ) : (
                      <AvatarPreview size={40} userId={chat.otherUser.id} />
                    )}
                  </IonAvatar>
                  <IonLabel>
                    <h2>{chat.otherUser.display_name || chat.otherUser.username}</h2>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="outline"
                    size="small"
                    onClick={() => handleUnmuteChat(chat.id)}
                  >
                    <IonIcon icon={notifications} slot="start" />
                    Unmute
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )
        ) : (
          mutedGroups.length === 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}
              role="status"
              aria-label="No muted groups"
            >
              <IonText color="medium">
                <p>No muted groups</p>
              </IonText>
            </div>
          ) : (
            <IonList>
              {mutedGroups.map(group => (
                <IonItem key={group.id}>
                  <IonAvatar slot="start">
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'var(--ion-color-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      {group.name.charAt(0).toUpperCase()}
                    </div>
                  </IonAvatar>
                  <IonLabel>
                    <h2>{group.name}</h2>
                    <p>{group.member_count} members</p>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="outline"
                    size="small"
                    onClick={() => handleUnmuteGroup(group.id)}
                  >
                    <IonIcon icon={notifications} slot="start" />
                    Unmute
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )
        )}
      </IonContent>
    </IonModal>
  );
};
